/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-underscore-dangle */
import {
  Customer, Room, ImageFile, Portfolio, SignUp,
} from '@/types'

const _steps = {
  contact: {
    label: 'Contact info',
    routeName: 'SignupCustomer',
    isComplete: (state: any) => state.customerIsPersisted,
  },
  goals: {
    label: 'Project goals',
    routeName: 'SignupProjectGoals',
    isComplete: (state: any) => state.roomIsPersisted,
  },
  inspiration: {
    label: 'Inspiration photos',
    routeName: 'SignupInspirationPhotos',
    isComplete: (state: any) => state.inspirationFilesArePersisted,
  },
  room: {
    label: 'Room photos',
    routeName: 'SignupRoomPhotos',
    isComplete: (state: any) => state.roomFilesArePersisted,
  },
  measurements: {
    label: 'Measurements',
    routeName: 'SignupMeasurements',
    isComplete: (state: any) => state.dimensionsFilesArePersisted,
  },
  complete: {
    label: '',
    routeName: 'SignupComplete',
    isComplete: true,
  },
}

const initialState = {
  adminToken: '',
  apiBaseUrl: process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000',
  errorMessage: '',
  customer: {} as Customer,
  dimensionsFiles: [] as Array<ImageFile>,
  inspirationFiles: [] as Array<ImageFile>,
  isBusy: false,
  room: {} as Room,
  roomFiles: [] as Array<ImageFile>,
  selectedPortfolio: {} as Portfolio,
  selectedSignup: {} as SignUp,
  signupId: '',
  signupCode: '',
  signupType: { key: '', label: '' },
  customerIsPersisted: false,
  dimensionsFilesArePersisted: false,
  inspirationFilesArePersisted: false,
  roomIsPersisted: false,
  roomFilesArePersisted: false,
  signupSteps: {
    inhome: [
      _steps.contact,
    ],
    refresh: [
      _steps.contact,
    ],
    remodel: [
      _steps.contact,
    ],
  } as { [key: string]: Array<any> },
}

export default {
  state: initialState,

  clearErrorMessage() {
    this.state.errorMessage = ''
  },

  clearSelectedPortfolio() {
    this.state.selectedPortfolio = {} as Portfolio
  },

  clearSignup() {
    this.state = { ...initialState }
  },

  getNextStep(type: string, currentRouteName: string): any {
    const steps = this.state.signupSteps[type]
    if (!steps) {
      throw new Error(`No signup steps found for type '${type}'`)
    }

    const currentStep = steps.find(s => s.routeName === currentRouteName)
    if (!currentStep) {
      throw new Error(`No step '${currentRouteName}' found for ${type}`)
    }

    const nextIndex = steps.indexOf(currentStep) + 1
    return nextIndex === steps.length ? _steps.complete : steps[nextIndex]
  },

  getPreviousStep(type: string, currentRouteName: string): any {
    const steps = this.state.signupSteps[type]
    if (!steps) {
      throw new Error(`No signup steps found for type '${type}'`)
    }

    const currentStep = steps.find(s => s.routeName === currentRouteName)
    if (!currentStep) {
      throw new Error(`No step '${currentRouteName}' found for ${type}`)
    }

    const previousIndex = steps.indexOf(currentStep) - 1
    return previousIndex < 0 ? currentStep : steps[previousIndex]
  },

  getCurrentStepCount(type: string, currentRouteName: string): any {
    const steps = this.state.signupSteps[type]
    if (!steps) {
      throw new Error(`No signup steps found for type '${type}'`)
    }

    const currentStep = steps.find(s => s.routeName === currentRouteName)
    if (!currentStep) {
      throw new Error(`No step '${currentRouteName}' found for ${type}`)
    }

    return `${steps.indexOf(currentStep) + 1}/${steps.length}`
  },

  setAdminToken(token: string) {
    this.state.adminToken = token
  },

  setCustomer(customer: Customer) {
    this.state.customer = customer
  },

  setCustomerIsPersisted(value: boolean) {
    this.state.customerIsPersisted = value
  },

  setDimensionsFiles(files: Array<ImageFile>) {
    this.state.dimensionsFiles = files
  },

  setDimensionsFilesArePersisted(value: boolean) {
    this.state.dimensionsFilesArePersisted = value
  },

  setErrorMessage(message: string) {
    this.state.errorMessage = message
  },

  setInspirationFiles(files: Array<ImageFile>) {
    this.state.inspirationFiles = files
  },

  setInspirationFilesArePersisted(value: boolean) {
    this.state.inspirationFilesArePersisted = value
  },

  setIsBusy(value: boolean) {
    this.state.isBusy = value
  },

  setRoom(room: Room) {
    this.state.room = room
  },

  setRoomFiles(files: Array<ImageFile>) {
    this.state.roomFiles = files
  },

  setRoomFilesArePersisted(value: boolean) {
    this.state.roomFilesArePersisted = value
  },

  setRoomIsPersisted(value: boolean) {
    this.state.roomIsPersisted = value
  },

  setSelectedPortfolio(portfolio: Portfolio) {
    this.state.selectedPortfolio = portfolio
  },

  setSelectedSignup(signup: SignUp) {
    this.state.selectedSignup = signup
  },

  setSignupCode(code: string) {
    this.state.signupCode = code
  },

  setSignupId(id: string) {
    this.state.signupId = id
  },

  setSignupType(key: string, label: string) {
    this.state.signupType = { key, label }
  },
}
